/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { HiOutlineArrowRight } from "react-icons/hi";
import styled, { keyframes, css } from "styled-components";

const HeroSlider = ({ heroImages }) => {
  if (!heroImages || !Array.isArray(heroImages) || heroImages.length === 0) {
    return null;
  }

  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoplayTimerRef = useRef(null);

  const SLIDE_DURATION = 5000; // 5 seconds per auto-cycle

  // Autoplay effect
  useEffect(() => {
    if (!isHovered && heroImages.length > 0) {
      autoplayTimerRef.current = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % heroImages.length);
      }, SLIDE_DURATION);
    }
    return () => {
      if (autoplayTimerRef.current) clearInterval(autoplayTimerRef.current);
    };
  }, [isHovered, heroImages.length]);

  const handleCardMouseEnter = (index) => {
    setIsHovered(true);
    setActiveIndex(index);
  };

  const handleCardMouseLeave = () => {
    setIsHovered(false);
  };

  const activeData = heroImages[activeIndex] || heroImages[0] || {};

  // Premium, highly curated color theme palettes mapping directly to global design tokens:
  // Kerala Bark (#2D1F18), Kerala Green (#123524), and Kerala Gold (#C5A880)
  const slideThemes = [
    {
      bg: "linear-gradient(135deg, #2D1F18 0%, #120c0a 100%)",
      glow: "rgba(197, 168, 128, 0.12)",
      badgeBg: "rgba(197, 168, 128, 0.08)",
      badgeText: "#C5A880",
      badgeBorder: "rgba(197, 168, 128, 0.2)",
      textAccent: "#C5A880"
    },
    {
      bg: "linear-gradient(135deg, #123524 0%, #07150e 100%)",
      glow: "rgba(197, 168, 128, 0.15)",
      badgeBg: "rgba(197, 168, 128, 0.08)",
      badgeText: "#C5A880",
      badgeBorder: "rgba(197, 168, 128, 0.2)",
      textAccent: "#C5A880"
    },
    {
      bg: "linear-gradient(135deg, #4a3e02 0%, #1a1601 100%)",
      glow: "rgba(197, 168, 128, 0.12)",
      badgeBg: "rgba(197, 168, 128, 0.08)",
      badgeText: "#C5A880",
      badgeBorder: "rgba(197, 168, 128, 0.2)",
      textAccent: "#C5A880"
    }
  ];

  const activeTheme = slideThemes[activeIndex] || slideThemes[0];

  // Specific locations mapping for that high-end story vibe
  const cardMetadata = [
    {
      num: "01",
      label: "Rosewood Crafts",
      origin: "Malabar Artisans"
    },
    {
      num: "02",
      label: "Valley Spices",
      origin: "Munnar spice gardens"
    },
    {
      num: "03",
      label: "Golden Crisps",
      origin: "Local coconut orchards"
    }
  ];

  return (
    <ShowcaseContainer $bg={activeTheme.bg}>
      {/* Ambient background particles */}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-[radial-gradient(#C5A880_1px,transparent_1px)] [background-size:24px_24px] z-0" />
      <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full filter blur-3xl pointer-events-none transition-all duration-1000"
           style={{ backgroundColor: activeTheme.glow }} />
      <div className="absolute -left-24 -bottom-24 w-96 h-96 rounded-full bg-black/40 filter blur-3xl pointer-events-none" />

      <SplitLayout>
        {/* Left Column: Curated Story/Editorial Panel */}
        <LeftTextPanel>
          <div className="flex flex-col gap-6 relative z-10">
            {/* Dynamic Gold Badge */}
            <div className="inline-flex self-start items-center gap-2 px-4 py-1.5 rounded-full backdrop-blur-md border text-xs font-semibold tracking-widest uppercase font-jost transition-all duration-500"
                 style={{
                   backgroundColor: activeTheme.badgeBg,
                   borderColor: activeTheme.badgeBorder,
                   color: activeTheme.badgeText
                 }}>
              <span className="w-1.5 h-1.5 rounded-full animate-ping" style={{ backgroundColor: activeTheme.badgeText }} />
              Focusing on: {cardMetadata[activeIndex]?.label || activeData.title || "Kerala Specials"}
            </div>

            {/* Content Display */}
            <ContentTransitionBlock key={activeIndex}>
              <span className="block font-jost text-xs sm:text-sm font-semibold tracking-widest uppercase transition-all duration-500"
                    style={{ color: activeTheme.textAccent }}>
                {activeData.tagline || "HERITAGE OF PURITY"}
              </span>
              <h1 className="text-white font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mt-2 mb-4">
                {activeData.title || "Kerala Specials"}
              </h1>
              <p className="text-white/70 font-jost text-sm sm:text-base leading-relaxed max-w-xl text-justify mb-4">
                {activeData.desc || "Sustainably sourced brassware, organic teak wood, and premium products crafted by Kerala’s master artisans."}
              </p>
              
              <div className="mt-4">
                <ExploreButton href={activeData.route || "/"} $accent={activeTheme.badgeText}>
                  <span>Explore Collection</span>
                  <HiOutlineArrowRight />
                </ExploreButton>
              </div>
            </ContentTransitionBlock>
          </div>

          {/* Simple Tab progress index */}
          <ProgressIndex $accent={activeTheme.badgeText}>
            <span className="active-num">0{activeIndex + 1}</span>
            <div className="divider-line" />
            <span className="total-num">0{heroImages.length}</span>
          </ProgressIndex>
        </LeftTextPanel>

        {/* Right Column: Staggered Overlapping 3D Collage Frame */}
        <RightVisualPanel>
          <CollageStage>
            {heroImages.map((data, index) => {
              const isActive = index === activeIndex;
              const meta = cardMetadata[index] || { num: `0${index + 1}`, label: data.title || "Kerala Gems", origin: "Kerala, India" };
              const theme = slideThemes[index] || slideThemes[0];
              
              return (
                <CollageCard
                  key={data.id || index}
                  $index={index}
                  $isActive={isActive}
                  $activeColor={theme.badgeText}
                  onMouseEnter={() => handleCardMouseEnter(index)}
                  onMouseLeave={handleCardMouseLeave}
                  onClick={() => setActiveIndex(index)}
                >
                  {/* Subtle Inner Border Glow */}
                  <div className="absolute inset-0 rounded-2xl border border-white/10 pointer-events-none z-10 transition-colors duration-300 group-hover:border-[#C5A880]/30" />
                  
                  {/* Image Element */}
                  <CardImg 
                    src={data.src} 
                    alt={data.title || "Slide Image"}
                    $isActive={isActive}
                  />

                  {/* Glassmorphic Metadata Overlay */}
                  <CardOverlay $isActive={isActive} $accent={theme.badgeText}>
                    <div className="flex flex-col text-left">
                      <span className="num">{meta.num}</span>
                      <span className="title">{meta.label}</span>
                      <span className="origin">{meta.origin}</span>
                    </div>
                  </CardOverlay>
                </CollageCard>
              );
            })}
          </CollageStage>
        </RightVisualPanel>
      </SplitLayout>
    </ShowcaseContainer>
  );
};

// Styled Components
const ShowcaseContainer = styled.div`
  position: relative;
  width: 100%;
  background: ${(props) => props.$bg || "#111512"};
  border-radius: 36px;
  overflow: hidden;
  border: 1px solid rgba(197, 168, 128, 0.15);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.25);
  transition: background 0.8s cubic-bezier(0.16, 1, 0.3, 1);
`;

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  min-height: 560px;
  width: 100%;

  @media screen and (max-width: 991px) {
    grid-template-columns: 1fr;
    min-height: auto;
  }
`;

const LeftTextPanel = styled.div`
  grid-column: span 6;
  padding: 64px 80px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  position: relative;
  z-index: 2;
  text-align: left;

  @media screen and (max-width: 1200px) {
    padding: 48px 56px;
  }

  @media screen and (max-width: 991px) {
    grid-column: span 12;
    order: 1;
    padding: 32px 24px 20px 24px;
    gap: 20px;
  }
`;

const RightVisualPanel = styled.div`
  grid-column: span 6;
  padding: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
  background: rgba(0, 0, 0, 0.12);
  border-left: 1px solid rgba(197, 168, 128, 0.08);
  overflow: hidden;

  @media screen and (max-width: 1200px) {
    padding: 32px;
  }

  @media screen and (max-width: 991px) {
    grid-column: span 12;
    order: 2;
    border-left: none;
    border-bottom: 1px solid rgba(197, 168, 128, 0.08);
    padding: 24px 20px 48px 20px;
    min-height: 400px;
  }

  @media screen and (max-width: 480px) {
    min-height: 320px;
    padding: 24px 16px 36px 16px;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ContentTransitionBlock = styled.div`
  animation: ${fadeInUp} 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const ExploreButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 32px;
  border-radius: 50px;
  background: linear-gradient(135deg, ${(props) => props.$accent || "#D4AF37"} 0%, #C5A880 100%);
  color: #123524 !important;
  font-family: 'Jost', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 8px 25px rgba(197, 168, 128, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.15);
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;

  svg {
    font-size: 16px;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 35px rgba(212, 175, 55, 0.35);
    background: #ffffff;
    color: #123524 !important;
    
    svg {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(-1px);
  }
`;

const ProgressIndex = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: auto;
  border-top: 1px solid rgba(197, 168, 128, 0.10);
  padding-top: 24px;
  font-family: 'Poppins', sans-serif;

  .active-num {
    font-size: 20px;
    font-weight: 700;
    color: ${(props) => props.$accent || "#C5A880"};
    transition: color 0.5s ease;
  }

  .divider-line {
    width: 48px;
    height: 1px;
    background: rgba(197, 168, 128, 0.3);
  }

  .total-num {
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.3);
  }
`;

// Staggered 3D Stage Setup
const CollageStage = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  max-width: 450px;
  margin: 0 auto;

  @media screen and (max-width: 480px) {
    height: 320px;
    max-width: 320px;
  }
`;

// Floating micro-animation keyframes for card layers
const cardFloat1 = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-3deg); }
  50% { transform: translateY(-8px) rotate(-2deg); }
`;
const cardFloat2 = keyframes`
  0%, 100% { transform: translateY(0px) rotate(4deg); }
  50% { transform: translateY(-6px) rotate(5deg); }
`;
const cardFloat3 = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-1deg); }
  50% { transform: translateY(-10px) rotate(0deg); }
`;

// Collage Card Styling
const CollageCard = styled.div`
  position: absolute;
  border-radius: 20px;
  overflow: hidden;
  background: #0d120f;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(197, 168, 128, 0.15);
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  
  /* Staggered positioning mapping */
  ${(props) => {
    // Card 1: Crafts (Top Left)
    if (props.$index === 0) {
      return css`
        width: 250px;
        height: 250px;
        top: 10px;
        left: 10px;
        z-index: ${props.$isActive ? 15 : 3};
        animation: ${props.$isActive ? 'none' : css`${cardFloat1} 8s ease-in-out infinite`};
        transform: ${props.$isActive ? 'scale(1.08) translate(10px, 10px) rotate(0deg)' : 'rotate(-3deg)'};
        opacity: ${props.$isActive ? 1 : 0.65};
        box-shadow: ${props.$isActive ? '0 25px 60px rgba(197, 168, 128, 0.25)' : '0 10px 30px rgba(0,0,0,0.3)'};
        border-color: ${props.$isActive ? props.$activeColor : 'rgba(197, 168, 128, 0.15)'};

        @media screen and (max-width: 480px) {
          width: 180px;
          height: 180px;
        }
      `;
    }
    // Card 2: Spices (Middle Right)
    if (props.$index === 1) {
      return css`
        width: 230px;
        height: 230px;
        top: 60px;
        right: 10px;
        z-index: ${props.$isActive ? 15 : 2};
        animation: ${props.$isActive ? 'none' : css`${cardFloat2} 9s ease-in-out infinite 0.5s`};
        transform: ${props.$isActive ? 'scale(1.08) translate(-10px, 10px) rotate(0deg)' : 'rotate(4deg)'};
        opacity: ${props.$isActive ? 1 : 0.65};
        box-shadow: ${props.$isActive ? '0 25px 60px rgba(197, 168, 128, 0.25)' : '0 10px 30px rgba(0,0,0,0.3)'};
        border-color: ${props.$isActive ? props.$activeColor : 'rgba(197, 168, 128, 0.15)'};

        @media screen and (max-width: 480px) {
          width: 170px;
          height: 170px;
          top: 40px;
        }
      `;
    }
    // Card 3: Snacks (Bottom Center)
    return css`
      width: 220px;
      height: 220px;
      bottom: 10px;
      left: 100px;
      z-index: ${props.$isActive ? 15 : 1};
      animation: ${props.$isActive ? 'none' : css`${cardFloat3} 7s ease-in-out infinite 1s`};
      transform: ${props.$isActive ? 'scale(1.08) translate(0, -10px) rotate(0deg)' : 'rotate(-1deg)'};
      opacity: ${props.$isActive ? 1 : 0.65};
      box-shadow: ${props.$isActive ? '0 25px 60px rgba(197, 168, 128, 0.25)' : '0 10px 30px rgba(0,0,0,0.3)'};
      border-color: ${props.$isActive ? props.$activeColor : 'rgba(197, 168, 128, 0.15)'};

      @media screen and (max-width: 480px) {
        width: 160px;
        height: 160px;
        left: 70px;
      }
    `;
  }}

  &:hover {
    z-index: 20;
    opacity: 1;
    transform: scale(1.1) rotate(0deg) !important;
    box-shadow: 0 30px 70px rgba(197, 168, 128, 0.35);
    border-color: ${(props) => props.$activeColor || "#C5A880"};
  }
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;
  transform: ${(props) => (props.$isActive ? "scale(1.04)" : "scale(1)")};
`;

const CardOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.4) 60%, rgba(0, 0, 0, 0) 100%);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
  display: flex;
  align-items: flex-end;
  transition: opacity 0.3s ease;
  opacity: ${(props) => (props.$isActive ? 1 : 0)};
  pointer-events: none;

  ${CollageCard}:hover & {
    opacity: 1;
  }

  .num {
    font-family: 'Poppins', sans-serif;
    font-size: 10px;
    font-weight: 600;
    color: ${(props) => props.$accent || "#C5A880"};
    line-height: 1;
    margin-bottom: 2px;
    transition: color 0.3s ease;
  }

  .title {
    font-family: 'Jost', sans-serif;
    font-size: 12px;
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    line-height: 1.2;
  }

  .origin {
    font-family: 'Jost', sans-serif;
    font-size: 9px;
    color: rgba(255, 255, 255, 0.5);
    line-height: 1;
    margin-top: 2px;
  }
`;

export default HeroSlider;
